import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MaterialReactTable, useMaterialReactTable} from 'material-react-table';
import { Box, Button, CircularProgress, MenuItem, Select, Typography } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
//
import NotoSansRegular from '../../../assets/fonts/NotoSans-Regular.ttf';
import NotoSansBold from '../../../assets/fonts/NotoSans-Bold.ttf';
import NotoSansThaiRegular from '../../../assets/fonts/NotoSansThai-Regular.ttf';
import NotoSansThaiBold from '../../../assets/fonts/NotoSansThai-Bold.ttf';
import NotoNaskhArabicRegular from '../../../assets/fonts/NotoNaskhArabic-Regular.ttf';
import NotoNaskhArabicBold from '../../../assets/fonts/NotoNaskhArabic-Bold.ttf';
import NotoSansDevanagariRegular from '../../../assets/fonts/NotoSansDevanagari-Regular.ttf';
import NotoSansDevanagariBold from '../../../assets/fonts/NotoSansDevanagari-Bold.ttf';
//
// import * as XLSX from 'xlsx';
import * as XLSX from 'xlsx-js-style';
import { saveAs } from "file-saver";
import moment from 'moment';
import { toAbsoluteUrl } from '../../../_metronic/helpers';
import { useAppSelector } from '../../../app/hooks';
import { determineCommaSeprater, FormatInvoiceDecimalNumber, determineCultureDateTimeFormat, determineCultureFormat, formatDateWithTime, formatCurrntDateTime } from '../../../common/timeLiveFunction';
import { parseISO, format, isValid, parse } from "date-fns"
import { formatAuditValue } from './AuditTrailFunctions';
import ExportToPDFModal from './ExportToPDFModal';

const loadFontAsBase64 = async (fontPath: string): Promise<string> => {
    const response = await fetch(fontPath);
    if (!response.ok) {
        throw new Error(`Failed to load font: ${fontPath}`);
    }
    const arrayBuffer = await response.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    let binary = "";
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
        const chunk = bytes.subarray(
            i,
            Math.min(i + chunkSize, bytes.length)
        );
        binary += String.fromCharCode.apply(
            null,
            Array.from(chunk)
        );
    }
    return btoa(binary);
};

const getPDFFontForText = (text: string): string => {
    if (!text) {
        return "NotoSans";
    }
    // Thai
    if (/[\u0E00-\u0E7F]/.test(text)) {
        return "NotoSansThai";
    }
    // Arabic / Persian / Urdu
    if (/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/.test(text)) {
        return "NotoNaskhArabic";
    }
    // Hindi / Devanagari
    if (/[\u0900-\u097F]/.test(text)) {
        return "NotoSansDevanagari";
    }
    // English / Latin
    return "NotoSans";
};

type DayMap = {
    Mon: string;
    Tue: string;
    Wed: string;
    Thu: string;
    Fri: string;
    Sat: string;
    Sun: string;
};

const MUITableView = (props: any) => {
    const { reportName, selectedFilters } = props
    const { config = {}, data = {}, reportId, consolidated, companyLogo, showCompanyLogo } = props.table;
    const isSpecialReport = reportId?.toLowerCase() === "3003299d-18bb-41ae-ac21-204cf6a372a1";
    const rowVirtualizerInstanceRef = useRef<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const { decimalPlaces, isCompanyOwnLogo, cultureInfoName } = useAppSelector(state => state.globalState);
    const isCommaSeparator = determineCommaSeprater(cultureInfoName.toString());
    const [isOpen, setIsOpen] = useState(false);
    const [exportLoading, setExportLoading] = useState({
        pdf: false,
        csv: false,
        xls: false
    });

    const flattenData = (dataObj: any) => {
        let flatData: any[] = [];
        const flatten = (item: any) => {
            if (consolidated && Array.isArray(item)) {
                return;
            }

            if (item?.subGroups?.items && Array.isArray(item.subGroups.items)) {
                flatData = flatData.concat(item.subGroups.items);
            }
            Object.values(item?.subGroups || {}).forEach(flatten);
        };
        Object.values(dataObj || {}).forEach(flatten);
        return flatData;
    };

    const processSpecialReport = (rawData: any) => {
        const getDayLabels = (data: any) => {
            if (!data?.length) return {};
            const firstRecord = data[0];
            return Object.keys(firstRecord)
                .filter((key: any) => !isNaN(key) && key >= 1 && key <= 7)
                .sort((a: any, b: any) => a - b)
                .reduce((acc: any, key: any) => {
                    const value = firstRecord[key];
                    const [dayAbbreviation] = value ? value.split(" - ") : [""];
                    const dayMap: DayMap = {
                        Mon: "Monday",
                        Tue: "Tuesday",
                        Wed: "Wednesday",
                        Thu: "Thursday",
                        Fri: "Friday",
                        Sat: "Saturday",
                        Sun: "Sunday",
                    };
                    acc[key] = dayMap[dayAbbreviation as keyof DayMap] || dayAbbreviation;
                    return acc;
                }, {});
        };

        const flatData = flattenData(rawData);
        const dayLabels = getDayLabels(flatData);

        return {
            columns: [
                {
                    accessorKey: "EmployeeName",
                    header: "Employee Name",
                    enableGrouping: true,
                    size: 200,
                    GroupedCell: ({ cell, row }: any) => (
                        <Box sx={{ paddingLeft: `${row.depth * 16}px` }}>
                            {cell.getValue()}
                        </Box>
                    )
                },
                ...Object.entries(dayLabels).map(([key, label]) => ({
                    accessorKey: key,
                    header: label,
                    size: 150,
                    Cell: ({ cell }: any) => {
                        const value = cell.getValue();
                        return <div style={{ whiteSpace: "nowrap" }}>{value ? value.split(" - ").slice(1).join(" - ") : ""}</div>;
                    },
                    GroupedCell: ({ cell, row }: any) => (
                        <Box sx={{ paddingLeft: `${row.depth * 16}px` }}>
                            {cell.getValue()}
                        </Box>
                    )
                })),
            ],
            data: flatData,
        };
    };

    const groupKeys = useMemo(() => config.groups?.map((item: any) => item.accessorKey) || [], [config]);

    const formatTimeWithoutDate = (dateString: any) => {
        if (!dateString || typeof dateString !== 'string') return '';

        const momentObj = moment(dateString);

        if (!momentObj.isValid()) {
            return '';
        }

        return momentObj.format('H:mm');
    };

    const configColumns = useMemo(() => {
        const groups = config.groups || [];
        const summaryConfig = config.summaryConfig || [];
        const groupAccessorKeys = groups.map((g: any) => g.accessorKey?.toLowerCase?.());
        const getFooterRenderer = (col: any, rows: any[]) => {
            const decimalPointForHours = col?.accessorKey.toLowerCase() === 'totalhours' || col?.accessorKey.toLowerCase() === 'earned' || col?.accessorKey.toLowerCase() === 'consume' || col?.accessorKey.toLowerCase() === 'available' || col?.accessorKey.toLowerCase() === 'carryforward' || col?.accessorKey.toLowerCase() === 'initialsettohours' || col?.accessorKey.toLowerCase() === 'policyearnhours' || col?.accessorKey.toLowerCase() === 'resettohours' || col?.accessorKey.toLowerCase().includes('hours') || col?.accessorKey.toLowerCase().includes('hour');
            const footerType = col.footer?.toLowerCase();
            const values = rows.map(row => row.original[col.accessorKey]);
            const total = values.reduce((sum, val) => sum + (typeof val === 'number' ? val : 0), 0);

            if (footerType === 'count') return decimalPointForHours ? FormatInvoiceDecimalNumber(cultureInfoName.toString(), rows.length, 2) : FormatInvoiceDecimalNumber(cultureInfoName.toString(), rows.length, decimalPlaces) //rows.length;
            if (footerType === 'sum') return decimalPointForHours ? FormatInvoiceDecimalNumber(cultureInfoName.toString(), total, 2) : FormatInvoiceDecimalNumber(cultureInfoName.toString(), total, decimalPlaces);//Number(total).toFixed(decimalPlaces);
            return '';
        };

        const formatCurrencyold = (col: any, value: any, currencyCode: string) => {
            // Handle empty/null values
            if (value === undefined || value === null || value === '') {
                return '';
            }

            // Handle invalid inputs - FIX: Check for blank currencyCode here
            if (!currencyCode || currencyCode === '' || typeof value !== 'number') {
                if (typeof value === 'number') {
                    return FormatInvoiceDecimalNumber(cultureInfoName.toString(), value, 2);
                }
                return value;
            }

            try {
                // Determine locale
                let locale = cultureInfoName.toString();
                if (locale === '' || locale === 'auto') {
                    locale = 'en-US';
                }

                // Normalize currency code - handle US$ and blank
                const normalizedCurrency = currencyCode === 'US$' ? 'USD' : currencyCode;

                return new Intl.NumberFormat(locale + "-u-nu-latn", {
                    style: 'currency',
                    currency: normalizedCurrency,
                    minimumFractionDigits: decimalPlaces,
                    maximumFractionDigits: decimalPlaces
                }).format(value);

            } catch (error) {
                console.error('Currency formatting error:', error);
                return value;
            }
        };

        const formatCurrency = (col: any, value: any, currencyCode: string) => {

            // Determine locale
            let locale = cultureInfoName.toString();
            if (locale === '' || locale === 'auto') {
                locale = 'en-US';
            }

            if (value === undefined || value === null || value === '') {
                return '';
            }
            if (!currencyCode || typeof value !== 'number') {
                if (typeof value === 'number')
                    return FormatInvoiceDecimalNumber(cultureInfoName.toString(), value, decimalPlaces);
            }

            try {
                if (isCommaSeparator) {
                    if (col.type?.toLowerCase?.() === "currencydecimal" && (col.accessorKey.toLowerCase() === 'basecurrencytaxamount' || col.accessorKey.toLowerCase() === 'reimbursementamount' || col.accessorKey.toLowerCase() === 'billingrate' || col.accessorKey.toLowerCase() === 'basecurrencyreimbursementamount' || col.accessorKey.toLowerCase() === 'basecurrencynetamount' || col.accessorKey.toLowerCase() === 'basecurrencyemployeecost' || col.accessorKey.toLowerCase() === 'basecurrencyprofit' || col.accessorKey.toLowerCase() === 'amount' || col.accessorKey.toLowerCase() === 'basecurrencyamount' || col.accessorKey.toLowerCase() === 'taxamount' || col.accessorKey.toLowerCase() === 'netamount')) {
                        // Use Intl.NumberFormat for European formatting
                        return new Intl.NumberFormat(locale.toString() + "-u-nu-latn", {
                            style: 'currency',
                            currency: currencyCode === 'US$' ? 'USD' : currencyCode,
                            minimumFractionDigits: decimalPlaces,
                            maximumFractionDigits: decimalPlaces
                        }).format(value);
                    } else {
                        return new Intl.NumberFormat(locale.toString() + "-u-nu-latn", {
                            style: 'currency',
                            currency: currencyCode === 'US$' ? 'USD' : currencyCode,
                            minimumFractionDigits: decimalPlaces,
                            maximumFractionDigits: decimalPlaces
                        }).format(value);
                    }
                }
                if (isCommaSeparator === false) {
                    if (col.type?.toLowerCase?.() === "currencydecimal" && (col.accessorKey.toLowerCase() === 'basecurrencytaxamount' || col.accessorKey.toLowerCase() === 'reimbursementamount' || col.accessorKey.toLowerCase() === 'billingrate' || col.accessorKey.toLowerCase() === 'basecurrencyreimbursementamount' || col.accessorKey.toLowerCase() === 'basecurrencynetamount' || col.accessorKey.toLowerCase() === 'basecurrencyemployeecost' || col.accessorKey.toLowerCase() === 'basecurrencyprofit' || col.accessorKey.toLowerCase() === 'amount' || col.accessorKey.toLowerCase() === 'basecurrencyamount' || col.accessorKey.toLowerCase() === 'taxamount' || col.accessorKey.toLowerCase() === 'netamount')) {

                        return new Intl.NumberFormat(locale.toString() + "-u-nu-latn", {
                            style: 'currency',
                            currency: currencyCode === 'US$' ? 'USD' : currencyCode,
                            minimumFractionDigits: decimalPlaces,
                            maximumFractionDigits: decimalPlaces
                        }).format(value);
                    } else {
                        return new Intl.NumberFormat(locale.toString() + "-u-nu-latn", {
                            style: 'currency',
                            currency: currencyCode === 'US$' ? 'USD' : currencyCode,
                            minimumFractionDigits: decimalPlaces,
                            maximumFractionDigits: decimalPlaces
                        }).format(value);
                    }

                }

            } catch (error) {
                return value;
            }
        };

        const renderGroupedCell = (value: any, row: any) => (
            <Box sx={{ paddingLeft: `${row.depth * 16}px` }}>{value}</Box>
        );

        const cols = (config.columns || []).map((col: any) => {
            const footerType = col.footer?.toLowerCase?.();
            const isSum = footerType === 'sum';
            const isCount = footerType === 'count';

            const baseConfig: any = {
                ...col,
                aggregationFn: isSum ? 'sum' : isCount ? 'count' : undefined,
                GroupedCell: ({ cell, row, table }: any) => renderGroupedCell(cell.getValue(), row),
            };

            switch (col.type?.toLowerCase?.()) {
                case 'date':
                    return {
                        ...baseConfig,
                        AggregatedCell: ({ cell }: any) => <strong>{cell.getValue() != null ? FormatInvoiceDecimalNumber(cultureInfoName.toString(), cell.getValue(), decimalPlaces) : cell.getValue()}</strong>,
                        // Cell: ({ cell }: any) => formatDateWithoutTimezone(cell.getValue()),
                        Cell: ({ cell }: any) => {
                            const cellValue = cell.getValue();
                            if (cellValue && typeof cellValue === 'string') {
                                try {
                                    const parsedDate = parseISO(cellValue);
                                    const formattedDate = format(parsedDate, determineCultureFormat(cultureInfoName.toString()));
                                    return consolidated ? null : formattedDate;
                                } catch (error) {
                                    console.error("Error parsing date:", error);
                                    return "";
                                }
                            }

                            return "";
                        },
                        Footer: ({ table }: any) => (
                            <strong style={{ fontSize: '14px' }}>
                                {getFooterRenderer(col, table.getPreFilteredRowModel().rows)}
                            </strong>
                        ),

                    };
                case 'datetime':
                    return {
                        ...baseConfig,
                        AggregatedCell: ({ cell }: any) => <strong>{cell.getValue()}</strong>,
                        // Cell: ({ cell }: any) => formatDateWithoutTimezone(cell.getValue()),
                        Cell: ({ cell }: any) => {
                            const cellValue = cell.getValue();
                            if (cellValue && typeof cellValue === 'string') {
                                try {
                                    const formattedDate = formatCurrntDateTime(cellValue, cultureInfoName?.toString())
                                    return consolidated ? null : formattedDate;
                                } catch (error) {
                                    console.error("Error parsing date:", error);
                                    return "";
                                }
                            }

                            return "";
                        },
                        Footer: ({ table }: any) => (
                            <strong style={{ fontSize: '14px' }}>
                                {getFooterRenderer(col, table.getPreFilteredRowModel().rows)}
                            </strong>
                        ),

                    };

                case 'time':
                case 'duration':
                    return {
                        ...baseConfig,
                        AggregatedCell: ({ cell }: any) => <strong>{cell.getValue() != null ? FormatInvoiceDecimalNumber(cultureInfoName.toString(), cell.getValue(), decimalPlaces) : cell.getValue()}</strong>,
                        // Cell: ({ cell }: any) => consolidated ? null : cell.getValue() === '0:00' ? cell.getValue() : formatTimeWithoutDate(cell.getValue()),
                        Cell: ({ cell }: any) => {
                            if (consolidated) return null;
                            const value = cell.getValue();
                            const isValidTime = moment(value, 'H:mm', true).isValid();
                            return consolidated ? null : isValidTime ? value : formatTimeWithoutDate(value);
                        },
                        Footer: ({ table }: any) => (
                            <strong style={{ fontSize: '14px' }}>
                                {getFooterRenderer(col, table.getPreFilteredRowModel().rows)}
                            </strong>
                        ),
                    };

                case 'decimal':
                    return {
                        ...baseConfig,
                        AggregatedCell: ({ cell }: any) => {
                            const decimalPointForHours = col?.accessorKey.toLowerCase() === 'totalhours' || col?.accessorKey.toLowerCase() === 'earned' || col?.accessorKey.toLowerCase() === 'consume' || col?.accessorKey.toLowerCase() === 'available' || col?.accessorKey.toLowerCase() === 'carryforward' || col?.accessorKey.toLowerCase() === 'initialsettohours' || col?.accessorKey.toLowerCase() === 'policyearnhours' || col?.accessorKey.toLowerCase() === 'resettohours' || col?.accessorKey.toLowerCase().includes('hours') || col?.accessorKey.toLowerCase().includes('hour');
                            const val = FormatInvoiceDecimalNumber(cultureInfoName.toString(), cell.getValue() ?? 0, decimalPointForHours ? 2 : decimalPlaces)//cell.getValue();
                            return <strong>{val != null ? val : null}</strong>
                        },
                        // AggregatedCell: ({ cell }: any) => <strong>{cell.getValue() ? Number(cell.getValue()).toFixed(2) : null}</strong>,
                        Cell: ({ cell }: any) => {
                            const decimalPointForHours = col?.accessorKey.toLowerCase() === 'totalhours' || col?.accessorKey.toLowerCase() === 'earned' || col?.accessorKey.toLowerCase() === 'consume' || col?.accessorKey.toLowerCase() === 'available' || col?.accessorKey.toLowerCase() === 'carryforward' || col?.accessorKey.toLowerCase() === 'initialsettohours' || col?.accessorKey.toLowerCase() === 'policyearnhours' || col?.accessorKey.toLowerCase() === 'resettohours' || col?.accessorKey.toLowerCase().includes('hours') || col?.accessorKey.toLowerCase().includes('hour');
                            return consolidated ? null : col.accessorKey.toLowerCase() === "taskdurationunit" || col.accessorKey.toLowerCase() === "projectestimateddurationunit" ? cell.getValue() : FormatInvoiceDecimalNumber(cultureInfoName.toString(), cell.getValue(), decimalPointForHours ? 2 : decimalPlaces);//Number(cell.getValue()).toFixed(2),
                        },
                        Footer: ({ table }: any) => {
                            return (
                                <strong style={{ fontSize: '12px' }}>
                                    {getFooterRenderer(col, table.getPreFilteredRowModel().rows)}
                                </strong>
                            )
                        },
                    };

                //case 'currencydecimal':
                //     return {
                //         ...baseConfig,
                //         Cell: ({ cell, row }: any) => {
                //             if (col.accessorKey.toLowerCase() === 'basecurrencytaxamount' || col.accessorKey.toLowerCase() === 'taxamount' || col.accessorKey.toLowerCase() === 'reimbursementamount' || col.accessorKey.toLowerCase() === 'billingrate' || col.accessorKey.toLowerCase() === 'basecurrencyreimbursementamount' || col.accessorKey.toLowerCase() === 'basecurrencynetamount' || col.accessorKey.toLowerCase() === 'basecurrencyemployeecost' || col.accessorKey.toLowerCase() === 'basecurrencyprofit' || col.accessorKey.toLowerCase() === 'amount' || col.accessorKey.toLowerCase() === 'basecurrencyamount' || col.accessorKey.toLowerCase() === 'taxamount' || col.accessorKey.toLowerCase() === 'netamount') {
                //                 return consolidated ? null : formatCurrency(col, cell.getValue(), row.original.ParameterCurrencyCode)
                //             } else {
                //                 return consolidated ? null : formatCurrency(col, cell.getValue(), row.original.CurrencyCode)
                //             }
                //         },
                //         AggregatedCell: ({ cell, row }: any) => {
                //             if (col.footer !== null) {
                //                 if (col.accessorKey.toLowerCase() === 'basecurrencytaxamount' || col.accessorKey.toLowerCase() === 'taxamount' || col.accessorKey.toLowerCase() === 'reimbursementamount' || col.accessorKey.toLowerCase() === 'billingrate' || col.accessorKey.toLowerCase() === 'basecurrencyreimbursementamount' || col.accessorKey.toLowerCase() === 'basecurrencynetamount' || col.accessorKey.toLowerCase() === 'basecurrencyemployeecost' || col.accessorKey.toLowerCase() === 'basecurrencyprofit' || col.accessorKey.toLowerCase() === 'amount' || col.accessorKey.toLowerCase() === 'basecurrencyamount' || col.accessorKey.toLowerCase() === 'taxamount' || col.accessorKey.toLowerCase() === 'netamount') {
                //                     return (
                //                         <strong>{formatCurrency(col, cell.getValue(), row.original.ParameterCurrencyCode)}</strong>
                //                     )
                //                 } else {
                //                     return (
                //                         <strong>{formatCurrency(col, cell.getValue(), row.original.CurrencyCode)}</strong>
                //                     )
                //                 }
                //             }
                //         },
                //         Footer: ({ table }: any) => {
                //             if (col.footer !== null) {
                //                 const footerType = col.footer?.toLowerCase();
                //                 const rows = table.getFilteredRowModel().rows;
                //                 const values = rows.map((row: any) => row.original[col.accessorKey]);
                //                 const total = values.reduce((sum: any, val: any) => sum + (typeof val === 'number' ? val : 0), 0);
                //                 const count = rows.length;
                //                 const rawTotal = footerType === 'count' ? count : footerType === 'sum' ? total : 0;
                //                 const currencyCode = (col.type?.toLowerCase?.() === 'currencydecimal' && (col.accessorKey.toLowerCase() === 'basecurrencytaxamount' || col.accessorKey.toLowerCase() === 'taxamount' || col.accessorKey.toLowerCase() === 'reimbursementamount' || col.accessorKey.toLowerCase() === 'billingrate' || col.accessorKey.toLowerCase() === 'basecurrencyreimbursementamount' || col.accessorKey.toLowerCase() === 'basecurrencynetamount' || col.accessorKey.toLowerCase() === 'basecurrencyemployeecost' || col.accessorKey.toLowerCase() === 'basecurrencyprofit' || col.accessorKey.toLowerCase() === 'amount' || col.accessorKey.toLowerCase() === 'basecurrencyamount' || col.accessorKey.toLowerCase() === 'taxamount' || col.accessorKey.toLowerCase() === 'netamount'))
                //                     ? table.getFilteredRowModel().rows?.[0]?.original?.ParameterCurrencyCode || 'USD'
                //                     : '';

                //                 return (
                //                     <strong style={{ fontSize: '12px' }}>
                //                         {formatCurrency(col, rawTotal, currencyCode)}
                //                     </strong>
                //                 );

                //             }
                //         }

                //     };



           case 'currencydecimal': {
    const accessorLower = col.accessorKey.toLowerCase();
    const isBaseCurrencyCol = accessorLower.startsWith('basecurrency') || 
                              accessorLower === 'basecurrencytaxamount' || 
                              accessorLower === 'basecurrencyreimbursementamount' || 
                              accessorLower === 'basecurrencynetamount' || 
                              accessorLower === 'basecurrencyemployeecost' || 
                              accessorLower === 'basecurrencyprofit' || 
                              accessorLower === 'basecurrencyamount';
    return {
        ...baseConfig,
        Cell: ({ cell, row }: any) => {
            if (consolidated) return null;

            const currencyCode = isBaseCurrencyCol 
                ? row.original.ParameterCurrencyCode 
                : row.original.CurrencyCode;

            return formatCurrency(col, cell.getValue(), currencyCode);
        },
        AggregatedCell: ({ cell, row }: any) => {
            if (col.footer !== null) {
                
                if (!isBaseCurrencyCol) return <span></span>;

                const subRows = row.subRows || [];
                const baseCurrencyCode = subRows[0]?.original?.ParameterCurrencyCode || 'USD';

                const totalConverted = subRows.reduce((sum: any, subRow: any) => {
                    const rData = subRow.original || {};
                    let val = 0;

                    if (accessorLower === 'basecurrencyamount') val = rData.BaseCurrencyAmount ?? rData.baseCurrencyAmount ?? 0;
                    else if (accessorLower === 'basecurrencytaxamount') val = rData.BaseCurrencyTaxAmount ?? rData.baseCurrencyTaxAmount ?? 0;
                    else if (accessorLower === 'basecurrencynetamount') val = rData.BaseCurrencyNetAmount ?? rData.baseCurrencyNetAmount ?? 0;
                    else if (accessorLower === 'basecurrencyreimbursementamount') val = rData.BaseCurrencyReimbursementAmount ?? rData.baseCurrencyReimbursementAmount ?? 0;
                    else val = rData[col.accessorKey] || 0;

                    return sum + (typeof val === 'number' ? val : Number(val) || 0);
                }, 0);

                return (
                    <strong>{formatCurrency(col, totalConverted, baseCurrencyCode)}</strong>
                );
            }
            return null;
        },
        Footer: ({ table }: any) => {
            if (col.footer !== null) {
               
                if (!isBaseCurrencyCol) return <span></span>;

                const footerType = col.footer?.toLowerCase();
                const rows = table.getFilteredRowModel().rows;
                const baseCurrencyCode = rows?.[0]?.original?.ParameterCurrencyCode || 'USD';
                
                const values = rows.map((row: any) => {
                    const rData = row.original || {};
                    
                    if (accessorLower === 'basecurrencyamount') return rData.BaseCurrencyAmount ?? rData.baseCurrencyAmount ?? 0;
                    if (accessorLower === 'basecurrencytaxamount') return rData.BaseCurrencyTaxAmount ?? rData.baseCurrencyTaxAmount ?? 0;
                    if (accessorLower === 'basecurrencynetamount') return rData.BaseCurrencyNetAmount ?? rData.baseCurrencyNetAmount ?? 0;
                    if (accessorLower === 'basecurrencyreimbursementamount') return rData.BaseCurrencyReimbursementAmount ?? rData.baseCurrencyReimbursementAmount ?? 0;
                    return rData[col.accessorKey] || 0;
                });

                const total = values.reduce((sum: any, val: any) => sum + (typeof val === 'number' ? val : Number(val) || 0), 0);
                const count = rows.length;
                const rawTotal = footerType === 'count' ? count : footerType === 'sum' ? total : 0;

                return (
                    <strong style={{ fontSize: '12px' }}>
                        {formatCurrency(col, rawTotal, baseCurrencyCode)}
                    </strong>
                );
            }
            return null;
        }
    };
}

                 case 'number':
                    return {
                        ...baseConfig,
                        AggregatedCell: ({ cell }: any) => <strong>{cell.getValue() != null ? FormatInvoiceDecimalNumber(cultureInfoName.toString(), cell.getValue(), decimalPlaces) : cell.getValue()}</strong>,
                        // Cell: ({ cell }: any) => formatDateWithoutTimezone(cell.getValue()),
                        Cell: ({ cell }: any) => {
                            const cellValue = cell.getValue();
                            const headerText = col.header?.toString() ?? '';
                            if (headerText.toLowerCase().endsWith('id')) {
                                return <Box textAlign={'right'}>{cellValue}</Box>;
                            }
                            return <Box>{cellValue != null ? FormatInvoiceDecimalNumber(cultureInfoName.toString(), cellValue, decimalPlaces) : null}</Box>;
                        },
                        Footer: ({ table }: any) => (
                            <strong style={{ fontSize: '12px' }}>
                                {getFooterRenderer(col, table.getFilteredRowModel().rows)}
                            </strong>
                        ),

                    };

                case 'boolean':
                    return {
                        ...baseConfig,
                        AggregatedCell: ({ cell }: any) => <strong>{cell.getValue() != null ? FormatInvoiceDecimalNumber(cultureInfoName.toString(), cell.getValue(), decimalPlaces) : cell.getValue()}</strong>,
                        Cell: ({ cell }: any) => consolidated ? null : (cell.getValue() ? 'Yes' : 'No'),
                        Footer: ({ table }: any) => (
                            <strong style={{ fontSize: '12px' }}>
                                {getFooterRenderer(col, table.getFilteredRowModel().rows)}
                            </strong>
                        ),
                    };

                default:
                    break;
            }

            if (col.accessorKey?.toLowerCase() === "date" && col.type === "text") {
                const formatTextDateValue = (value: any) => {
                    if (!value) return "";
                    const inputFormat = "dd MMM yyyy";
                    try {
                        if (typeof value === 'string' && value.includes(" - ")) {
                            const [startStr, endStr] = value.split(" - ");
                            const parsedStart = parse(startStr.trim(), inputFormat, new Date());
                            const parsedEnd = parse(endStr.trim(), inputFormat, new Date());
                            if (isValid(parsedStart) && isValid(parsedEnd)) {
                                const cultureFormat = determineCultureFormat(cultureInfoName.toString());
                                return `${format(parsedStart, cultureFormat)} - ${format(parsedEnd, cultureFormat)}`;
                            }
                        }
                        const parsedDate = parse(value, inputFormat, new Date());
                        if (!isValid(parsedDate)) return value;
                        return format(parsedDate, determineCultureFormat(cultureInfoName.toString()));
                    } catch (err) {
                        console.error("Date parse error:", value, err);
                        return value;
                    }
                }

                return {
                    ...baseConfig,
                    AggregatedCell: ({ cell }: any) => <strong>{formatTextDateValue(cell.getValue())}</strong>,

                    GroupedCell: ({ cell, row }: any) => {
                        return (
                            <Box sx={{ paddingLeft: `${row.depth * 16}px` }}>
                                {formatTextDateValue(cell.getValue())}
                            </Box>
                        )
                    },
                    Cell: ({ cell }: any) => formatTextDateValue(cell.getValue()),

                    Footer: ({ table }: any) => (
                        <strong style={{ fontSize: '12px' }}>
                            {getFooterRenderer(col, table.getFilteredRowModel().rows)}
                        </strong>
                    ),
                };
            }
            if (col.accessorKey === "CreatedOn" || col.accessorKey === "ApprovedOn") {
                return {
                    ...baseConfig,
                    AggregatedCell: ({ cell }: any) => <strong>{cell.getValue()}</strong>,
                    Cell: ({ cell }: any) => {
                        const value = cell.getValue();
                        if (!value) return "";

                        try {
                            const parsedDate = parseISO(value);
                            if (!isValid(parsedDate)) return "";
                            return format(parsedDate, determineCultureDateTimeFormat(cultureInfoName.toString()));
                        } catch (err) {
                            console.error("Error parsing date:", value, err);
                            return "";
                        }
                    },
                    Footer: ({ table }: any) => (
                        <strong style={{ fontSize: '12px' }}>
                            {getFooterRenderer(col, table.getFilteredRowModel().rows)}
                        </strong>
                    ),
                }
            }
            if (col.accessorKey.toLowerCase() === 'lastlogintime') {
                return {
                    ...baseConfig,
                    AggregatedCell: ({ cell }: any) => <strong>{cell.getValue()}</strong>,
                    // Cell: ({ cell }: any) => formatDateWithoutTimezone(cell.getValue()),
                    Cell: ({ cell }: any) => {
                        const cellValue = cell.getValue();
                        if (cellValue && typeof cellValue === 'string') {
                            try {
                                const formattedDate = formatCurrntDateTime(cellValue, cultureInfoName?.toString())
                                return consolidated ? null : formattedDate;
                            } catch (error) {
                                console.error("Error parsing date:", error);
                                return "";
                            }
                        }

                        return "";
                    },
                    Footer: ({ table }: any) => (
                        <strong style={{ fontSize: '14px' }}>
                            {getFooterRenderer(col, table.getPreFilteredRowModel().rows)}
                        </strong>
                    ),
                }
            }
            if (col.accessorKey.toLowerCase() === 'estimatedduration') {
                return {
                    ...baseConfig,
                    AggregatedCell: ({ cell }: any) => <strong>{cell.getValue() != null ? FormatInvoiceDecimalNumber(cultureInfoName.toString(), cell.getValue(), decimalPlaces) : cell.getValue()}</strong>,
                    // Cell: ({ cell }: any) => formatDateWithoutTimezone(cell.getValue()),
                    Cell: ({ cell }: any) => {
                        const cellValue = cell.getValue();
                        return consolidated ? null : FormatInvoiceDecimalNumber(cultureInfoName.toString(), cellValue, decimalPlaces);
                    },
                    Footer: ({ table }: any) => (
                        <strong style={{ fontSize: '14px' }}>
                            {getFooterRenderer(col, table.getPreFilteredRowModel().rows)}
                        </strong>
                    ),
                }
            }
            if (col.accessorKey === 'OldValue' || col.accessorKey === 'NewValue') {
                return {
                    ...baseConfig,
                    AggregatedCell: ({ cell }: any) => <strong>{cell.getValue() != null ? FormatInvoiceDecimalNumber(cultureInfoName.toString(), cell.getValue(), decimalPlaces) : cell.getValue()}</strong>,

                    Cell: ({ cell, row }: any) => {
                        const value = cell.getValue();

                        const fieldName = row.original.FieldName || '';
                        return consolidated ? null : formatAuditValue(value, fieldName, cultureInfoName.toString(), decimalPlaces);
                    },
                    Footer: ({ table }: any) => (
                        <strong style={{ fontSize: '14px' }}>
                            {getFooterRenderer(col, table.getPreFilteredRowModel().rows)}
                        </strong>
                    ),
                }
            }

            if (col.type === 'CustomField1' || col.type === 'text' || footerType) {
                const textType = col.type === "text";
                return {
                    ...baseConfig,
                    AggregatedCell: ({ cell }: any) => <strong>{cell.getValue()}</strong>,
                    Cell: textType ? ({ cell }: any) => (
                        <div style={{
                            whiteSpace: 'normal',
                            wordWrap: 'break-word',
                            maxWidth: '200px'
                        }}>
                            {cell.getValue()}
                        </div>
                    ) : undefined,
                    Footer: ({ table }: any) => (
                        <strong style={{ fontSize: '12px' }}>
                            {getFooterRenderer(col, table.getFilteredRowModel().rows)}
                        </strong>
                    ),
                };
            }

            if (isSum) {
                return {
                    ...baseConfig,
                    Cell: ({ cell }: any) => cell.getValue(),
                    AggregatedCell: ({ cell, row }: any) => <strong>{formatCurrency(col, cell.getValue(), row.original.ParameterCurrencyCode)}</strong>,
                    Footer: ({ table }: any) => (
                        <strong style={{ fontSize: '14px' }}>
                            {getFooterRenderer(col, table.getPreFilteredRowModel().rows)}
                        </strong>
                    ),
                };
            }

            if (isCount) {
                return {
                    ...baseConfig,
                    AggregatedCell: ({ cell }: any) => <strong>{cell.getValue()}</strong>,
                    Footer: ({ table }: any) => (
                        <strong style={{ fontSize: '14px' }}>{table.getPreFilteredRowModel().rows.length}</strong>
                    ),
                };
            }

            return baseConfig;
        });

        const firstGroup = groups[0] || {};

        const groupColumns = groups.map((group: any, index: number) => {
            const isFirstGroup = index === 0;
            const originalCol = (config.columns || []).find((c: any) => c.accessorKey === group.accessorKey);
            const isTextDate = group.accessorKey?.toLowerCase() === 'date' && (group.type === 'text' || originalCol?.type === 'text');
            return {
                ...group,
                size: isFirstGroup ? 200 : undefined,
                Footer: ({ table }: any) => {
                    const footerType = group.footer?.toLowerCase?.();
                    const isFirstColumn = table.getAllLeafColumns?.()?.[0]?.id === group.accessorKey;

                    if (footerType === 'count') {
                        const groupedRows = table.getGroupedRowModel().rows;

                        const getGroupedCount = (rows: any[], accessorKey: string): number => {
                            let total = 0;

                            rows.forEach((row) => {
                                if (row.groupingColumnId === accessorKey) {
                                    total += row.leafRows.length;
                                } else if (row.subRows?.length) {
                                    total += getGroupedCount(row.subRows, accessorKey);
                                }
                            });

                            return total;
                        };

                        const totalCount = getGroupedCount(groupedRows, group.accessorKey);
                        return (
                            <strong style={{ fontSize: '12px' }}>
                                {totalCount}
                            </strong>
                        );
                    }

                    if (isFirstColumn && !footerType && summaryConfig.length > 0) {
                        return (
                            <strong style={{ fontSize: '12px' }}>
                                Full Summary
                            </strong>
                        );
                    }

                    return null;
                },
                GroupedCell: ({ cell, row }: any) => {
                    const label = cell.getValue();
                    let displayLabel = label;

                    if (typeof label === 'string' && label.includes(' - ')) {
                        const [startDate, endDate] = label.split(' - ');
                        try {
                            // const parsedStart = parseISO(startDate);
                            // const parsedEnd = parseISO(endDate);
                            let parsedStart, parsedEnd;
                            if (isTextDate) {
                                const inputFormat = "dd MMM yyyy";
                                parsedStart = parse(startDate.trim(), inputFormat, new Date());
                                parsedEnd = parse(endDate.trim(), inputFormat, new Date());
                            } else {
                                parsedStart = parseISO(startDate);
                                parsedEnd = parseISO(endDate);
                            }
                            if (isValid(parsedStart) && isValid(parsedEnd)) {
                                const formattedStart = format(parsedStart, determineCultureFormat(cultureInfoName.toString()));
                                const formattedEnd = format(parsedEnd, determineCultureFormat(cultureInfoName.toString()));
                                displayLabel = `${formattedStart} - ${formattedEnd}`;
                            }
                            // const formattedStart = format(parsedStart, determineCultureFormat(cultureInfoName.toString()));
                            // const formattedEnd = format(parsedEnd, determineCultureFormat(cultureInfoName.toString()));
                            // displayLabel = `${formattedStart} - ${formattedEnd}`;
                        } catch (error) {
                            console.error("Error parsing grouped date range:", error);
                            displayLabel = label;
                        }
                    }
                    else if (
                        group.type?.toLowerCase?.() === 'date' ||
                        group.accessorKey?.toLowerCase?.() === 'approveddate' || isTextDate
                    ) {
                        try {
                            // const parsedDate = parseISO(label);
                            // displayLabel = format(parsedDate, determineCultureFormat(cultureInfoName.toString()));
                            let parsedDate;
                            if (isTextDate) {
                                parsedDate = parse(label, "dd MMM yyyy", new Date());
                            } else {
                                parsedDate = parseISO(label);
                            }

                            if (isValid(parsedDate)) {
                                displayLabel = format(parsedDate, determineCultureFormat(cultureInfoName.toString()));
                            }
                        } catch (error) {
                            console.error("Error parsing grouped date:", error);
                            displayLabel = "";
                        }
                    }

                    const aggregatedValue = row.leafRows?.length
                        ? getFooterRenderer(group, row.leafRows)
                        : null;
                    return (
                        <Box sx={{ paddingLeft: `${row.depth * 16}px`, fontWeight: 'bold' }}>
                            {displayLabel}
                            {aggregatedValue !== null && (
                                <span style={{ marginLeft: 8, color: '#888' }}>
                                    {aggregatedValue && `(${aggregatedValue})`}
                                </span>
                            )}
                        </Box>
                    );
                }

            };
        });

        const duplicateGroupCols = (config.columns || []).filter((col: any) =>
            groupAccessorKeys.includes(col.accessorKey?.toLowerCase?.())
        );

        const ungroupedDuplicates = !consolidated
            ? duplicateGroupCols.map((col: any) => {
                if (col.accessorKey?.toLowerCase() === "date" && col.type === "text") {
                    const formatTextDateValue = (value: any) => {
                        if (!value) return "";
                        const inputFormat = "dd MMM yyyy";
                        try {
                            if (typeof value === 'string' && value.includes(" - ")) {
                                const [startStr, endStr] = value.split(" - ");
                                const parsedStart = parse(startStr.trim(), inputFormat, new Date());
                                const parsedEnd = parse(endStr.trim(), inputFormat, new Date());
                                if (isValid(parsedStart) && isValid(parsedEnd)) {
                                    const cultureFormat = determineCultureFormat(cultureInfoName.toString());
                                    return `${format(parsedStart, cultureFormat)} - ${format(parsedEnd, cultureFormat)}`;
                                }
                            }
                            const parsedDate = parse(value, inputFormat, new Date());
                            if (!isValid(parsedDate)) return value;
                            return format(parsedDate, determineCultureFormat(cultureInfoName.toString()));
                        } catch (err) {
                            console.error("Date parse error:", value, err);
                            return value;
                        }
                    }

                    return {
                        ...col,
                        id: `${col.accessorKey}_ungrouped`,
                        header: `${col.header}`,
                        enableGrouping: false,
                        GroupedCell: undefined,
                        Cell: ({ cell }: any) => formatTextDateValue(cell.getValue()),
                        Footer: ({ table }: any) => (
                            <strong style={{ fontSize: '12px' }}>
                                {getFooterRenderer(col, table.getFilteredRowModel().rows)}
                            </strong>
                        ),
                    };
                }
                return ({
                    ...col,
                    id: `${col.accessorKey}_ungrouped`,
                    header: `${col.header}`,
                    enableGrouping: false,
                    GroupedCell: undefined,
                    Cell: ({ cell }: any) => {
                        if (consolidated) return null;

                        const cellValue = cell.getValue();

                        if (col.type === 'date' && typeof cellValue === 'string') {
                            try {
                                const parsedDate = parseISO(cellValue);
                                const formattedDate = format(
                                    parsedDate,
                                    determineCultureFormat(cultureInfoName.toString())
                                );
                                return formattedDate;
                            } catch (error) {
                                console.error('Error parsing date:', error);
                                return '';
                            }
                        }

                        return <div>{cellValue}</div>;
                    },
                    Footer: ({ table }: any) => (
                        <strong style={{ fontSize: '12px' }}>
                            {getFooterRenderer(col, table.getFilteredRowModel().rows)}
                        </strong>
                    ),
                })
            })
            : [];

        return [...groupColumns, ...cols, ...ungroupedDuplicates];
        // return [...groupColumns, ...cols];
    }, [config]);

    const processedReport = useMemo(() => { return (isSpecialReport ? processSpecialReport(data) : { columns: [], data: [] }) }, [isSpecialReport, data]);
    const { columns: processedColumns, data: processedData } = processedReport;
    const finalData = useMemo(() => (isSpecialReport ? processedData : flattenData(data)), [isSpecialReport, processedData, data]);
    const finalColumns = useMemo(() => (isSpecialReport ? processedColumns : configColumns), [isSpecialReport, processedColumns, configColumns]);
    const lastGroupKey = groupKeys[groupKeys.length - 1] || null;

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        try {
            rowVirtualizerInstanceRef.current?.scrollToIndex?.(0);
        } catch (error) {
            console.error(error);
        }
    }, []);

    const table = useMaterialReactTable({
        columns: finalColumns,
        // data: groupedDataOnly,
        data: finalData,
        enableExpanding: true,
        enablePagination: true,
        enableBottomToolbar: true,
        // enablePagination: false,
        // enableBottomToolbar: false,
        enableExpandAll: !consolidated,
        getRowCanExpand: (row) => {
            if (consolidated && row.depth > 0 && row.groupingColumnId === lastGroupKey) {
                return false;
            }
            return false;
        },
        muiTableHeadCellProps: ({ column }) => ({
            align:
                (column.columnDef as any).type?.toLowerCase() === "currencydecimal" ||
                    (column.columnDef as any).type?.toLowerCase() === "decimal" ||
                    (column.columnDef as any).type?.toLowerCase() === "basecurrencynetamount"
                    ? "right"
                    : "left",
            sx: {
                border: '1px solid #e7e7e7',

            },
        }),
        muiTableBodyCellProps: ({ column }) => ({
            align:
                (column.columnDef as any).type?.toLowerCase() === "currencydecimal" ||
                    (column.columnDef as any).type?.toLowerCase() === "decimal" ||
                    (column.columnDef as any).type?.toLowerCase() === "basecurrencynetamount"
                    ? "right"
                    : "left",
            sx: {
                border: '1px solid #e7e7e7',
            },
        }),
        muiTableFooterCellProps: ({ column }) => ({
            align:
                (column.columnDef as any).type?.toLowerCase() === "currencydecimal" ||
                    (column.columnDef as any).type?.toLowerCase() === "decimal" ||
                    (column.columnDef as any).type?.toLowerCase() === "basecurrencynetamount"
                    ? "right"
                    : "left",
        }),
        muiTableContainerProps: {
            sx: {
                '&::-webkit-scrollbar': {
                    width: '12px',
                    height: '12px',
                },
                '&::-webkit-scrollbar-track': {
                    backgroundColor: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#999',
                    borderRadius: '8px',
                },
                scrollbarWidth: 'auto',
                scrollbarColor: '#999 transparent',
                maxHeight: 'calc(100vh - 230px)',
            }
        },
        muiTableBodyRowProps: ({ row }) => ({
            sx: {
                backgroundColor: row.getIsGrouped() ? consolidated ? 'inherit' : '#f5f5f5' : 'inherit',
                height: consolidated ? row.getIsGrouped() ? 'auto' : 0 : 'auto',
                visibility: consolidated ? row.getIsGrouped() ? 'visible' : 'collapse' : 'visible',
            },
        }),
        layoutMode: 'semantic',
        displayColumnDefOptions: {
            "mrt-row-expand": {
                size: 0,
                muiTableBodyCellProps: {
                    sx: {
                        display: 'none',
                    },
                },
                muiTableHeadCellProps: {
                    sx: {
                        display: 'none',
                    },
                },
                muiTableFooterCellProps: {
                    sx: {
                        display: 'none',
                    },
                }
            },
            "mrt-row-numbers": {
                size: 10,
            },

            "mrt-row-select": {
                size: 10,
            },
        },
        muiTableFooterRowProps: {
            sx: {
                '& td': {
                    backgroundColor: '#f5f5f5',
                    borderBottom: '1px solid rgba(224, 224, 224, 1)',
                    position: 'relative'
                },
                '& td:first-of-type': {
                    fontWeight: 'bold',
                    textAlign: 'left',
                    paddingLeft: '16px !important'
                }
            }
        },
        enableColumnResizing: true,
        enableGrouping: true,
        enableStickyHeader: true,
        enableStickyFooter: true,
        enableGlobalFilter: false,
        enableColumnFilters: false,
        enableHiding: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableColumnActions: false,
        enableColumnDragging: false,
        initialState: {
            density: 'compact',
            expanded: true,
            grouping: isSpecialReport ? ['EmployeeName'] : groupKeys,
            sorting: groupKeys.length > 0 ? [{ id: groupKeys[0], desc: false }] : [],
            pagination: {
                pageIndex: 0,
                pageSize: 100,
            },
        },
        muiPaginationProps: {
            color: 'primary',
            shape: 'rounded',
            variant: 'outlined',
            showRowsPerPage: false,
        },
        paginationDisplayMode: 'pages',
        muiToolbarAlertBannerChipProps: { color: 'primary' },
        muiToolbarAlertBannerProps: {
            sx: { display: "none" }
        },
        state: { isLoading },
        rowVirtualizerInstanceRef,
        rowVirtualizerOptions: { overscan: 10 },
        renderTopToolbarCustomActions: ({ table }) => (
            <Box
                sx={{
                    display: 'flex',
                    gap: '16px',
                    padding: '8px',
                    flexWrap: 'wrap',
                }}
            >
                <Button
                    disabled={table.getPrePaginationRowModel().rows.length === 0 || exportLoading.pdf}
                    // onClick={handleExportPDF}
                    onClick={() => setIsOpen(true)}
                    startIcon={exportLoading.pdf ? <CircularProgress size={20} /> : <FileDownloadIcon />}
                // startIcon={<FileDownloadIcon />}
                >
                    {exportLoading.pdf ? 'Exporting...' : 'Export PDF'}
                </Button>
                <Button
                    disabled={table.getPrePaginationRowModel().rows.length === 0 || exportLoading.csv}
                    onClick={handleExportCSV}
                    startIcon={exportLoading.csv ? <CircularProgress size={20} /> : <FileDownloadIcon />}
                // startIcon={<FileDownloadIcon />}
                >
                    {exportLoading.csv ? 'Exporting...' : 'Export CSV'}
                </Button>
                <Button
                    disabled={table.getPrePaginationRowModel().rows.length === 0 || exportLoading.xls}
                    onClick={() => handleExportXLS()}
                    startIcon={exportLoading.xls ? <CircularProgress size={20} /> : <FileDownloadIcon />}
                // startIcon={<FileDownloadIcon />}
                >
                    {exportLoading.xls ? 'Exporting...' : 'Export XLSX'}
                </Button>
            </Box>
        ),
    });

    const handleExportCSV = async () => {
        setExportLoading(prev => ({ ...prev, csv: true }));
        try {
            const originalPagination = {
                pageIndex: table.getState().pagination.pageIndex,
                pageSize: table.getState().pagination.pageSize,
            };
            const totalRows = table.getPrePaginationRowModel().rows.length;
            table.setPagination({
                pageIndex: 0,
                pageSize: totalRows,
            });
            await new Promise(resolve => setTimeout(resolve, 1000));
            const tableElement = document.querySelector('.MuiTable-root');
            if (!tableElement) return;
 
            const headers = Array.from(tableElement.querySelectorAll('thead tr:last-child th'))
                .map(th => {
                    const header = th as HTMLElement;
                    let text = header.innerText.trim();
                    text = text.endsWith('0') ? text.slice(0, -1) : text;
                    return text.replace(/\n/g, ' ').replace(/\s+/g, ' ');//text.replace(/\n/g, ' ').replace(/\s+/g, ' ').replace(/,/g, '');
                });
 
            const rows = Array.from(tableElement.querySelectorAll('tbody tr'))
                .map(tr => Array.from(tr.querySelectorAll('td'))
                    .map(td => {
                        let text = td.innerText.trim();
                        return text.replace(/\n/g, ' ').replace(/\s+/g, ' ');//.replace(/\n/g, ' ').replace(/\s+/g, ' ').replace(/,/g, '');
                    })
                );
 
            const footerRows = Array.from(tableElement.querySelectorAll('tfoot tr'))
                .map(tr => Array.from(tr.querySelectorAll('td'))
                    .map(td => {
                        let text = td.innerText.trim();
                        return text.replace(/\n/g, ' ').replace(/\s+/g, ' '); //.replace(/\n/g, ' ').replace(/\s+/g, ' ').replace(/,/g, '');
                    })
                );
 
            let expandColumnIndex = -1;
            if (consolidated) {
                expandColumnIndex = headers.findIndex(header => header === '' || header.toLowerCase().includes('expand'));
            }
            const emptyColumns = headers.map((header, index) =>
                (!header && rows.every(row => !row[index]) && footerRows.every(row => !row[index])) ||
                (consolidated && index === expandColumnIndex)
            );
 
            const filteredHeaders = headers.filter((_, index) => !emptyColumns[index]);
            // const filteredRows = rows.map(row => row.filter((_, index) => !emptyColumns[index]));
            let filteredRows = rows.map(row => row.filter((_, index) => !emptyColumns[index]));
            filteredRows = filteredRows.filter(row => row.some(cell => cell && cell.trim() !== ''));
 
            const filteredFooterRows = footerRows.map(row => row.filter((_, index) => !emptyColumns[index]));
 
            if (filteredHeaders.length === 0 || filteredRows.every(row => row.every(cell => !cell))) {
                return;
            }
 
            const csvData = [];
 
            csvData.push([reportName]);
 
            if (selectedFilters && Object.keys(selectedFilters).length > 0) {
                Object.entries(selectedFilters).forEach(([key, value]) => {
                    csvData.push([value]);
                });
            }
 
            csvData.push([]);
 
            csvData.push(filteredHeaders);
 
            filteredRows.forEach(row => csvData.push(row));
 
            filteredFooterRows.forEach(row => csvData.push(row));
            const csvContent = csvData.map(row =>
                row.map(cell => {
                    let text = String(cell).replace(/"/g, '""');
                    if (/^\d{2}-\d{2}-\d{2}$/.test(text)) {
                        text = `'${text}`;
                    }
 
                    return text.includes(',') || text.includes('"') ? `"${text}"` : text;
                }).join(',')
            ).join('\n');
 
            // const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const BOM = '\uFEFF';
            const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
 
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
 
            const now = new Date();
            const filename = `LiveReport`;
 
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
 
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            table.setPagination(originalPagination);
 
        } catch (error) {
            console.log(error);
        } finally {
            setExportLoading(prev => ({ ...prev, csv: false }));
        }
    };
    
    const handleExportPDF = async (settings: any) => {
        setExportLoading(prev => ({ ...prev, pdf: true }));
        const { orientation, format, tableWidth } = settings;
        try {
            const originalPagination = {
                pageIndex: table.getState().pagination.pageIndex,
                pageSize: table.getState().pagination.pageSize,
            };
            const totalRows = table.getPrePaginationRowModel().rows.length;
            table.setPagination({
                pageIndex: 0,
                pageSize: totalRows,
            });
            await new Promise(resolve => setTimeout(resolve, 1000));
            const tableElement = document.querySelector(".MuiTable-root") as HTMLElement;
            if (!tableElement) return;

            const tempContainer = document.createElement("div");
            tempContainer.style.position = "absolute";
            tempContainer.style.left = "-9999px";
            tempContainer.style.visibility = "hidden";
            document.body.appendChild(tempContainer);

            const tableClone = tableElement.cloneNode(true) as HTMLElement;
            tempContainer.appendChild(tableClone);

            let headers = Array.from(tableClone.querySelectorAll("thead tr th")).map((th) => {
                const header = th as HTMLElement;
                let text = header.textContent?.trim() || '';
                return text.endsWith("0") ? text.slice(0, -1) : text;
            });

            const rows = Array.from(tableClone.querySelectorAll("tbody tr")).map((row) =>
                Array.from(row.querySelectorAll("td")).map((td) => {
                    let text = td.textContent?.trim() || '';
                    text = text.replace(/\u200F|\u200E|\u202A|\u202B|\u202C|\u202D|\u202E/g, '');
                    text = text.replace(/(\d)\s+(?=\d)/g, '$1');
                    return text;
                })
            );

            const footerRows = Array.from(tableElement.querySelectorAll("tfoot tr")).map((row) =>
                Array.from(row.querySelectorAll("td")).map((td) => {
                    let text = td.textContent?.trim() || '';
                    text = text.replace(/\u200F|\u200E|\u202A|\u202B|\u202C|\u202D|\u202E/g, ''); 
                    text = text.replace(/(\d)\s+(?=\d)/g, '$1');
                    return text;
                })
            );

            let expandColumnIndex = -1;
            if (consolidated) {
                expandColumnIndex = headers.findIndex(header => header === '' || header.toLowerCase().includes('expand'));
            }

            const emptyColumns = headers.map((header, index) =>
                (!header && rows.every(row => !row[index]) && footerRows.every(row => !row[index])) ||
                (consolidated && index === expandColumnIndex)
            );

            const filteredHeaders = headers.filter((_, index) => !emptyColumns[index]);
            // const filteredRows = rows.map((row) => row.filter((_, index) => !emptyColumns[index]));
            let filteredRows = rows.map((row) => row.filter((_, index) => !emptyColumns[index]));
            filteredRows = filteredRows.filter(row => row.some(cell => cell && cell.toString().trim() !== ''));
            const filteredFooterRows = footerRows.map((row) => row.filter((_, index) => !emptyColumns[index]));

            if (filteredHeaders.length === 0) {
                document.body.removeChild(tempContainer);
                alert("No valid data to export.");
                return;
            }

            const pageFormat = format;
            const userOrientation = orientation;
            const finalFontSize = 8;
            const finalCellPadding = 2;

            const pdf = new jsPDF({
                orientation: userOrientation as any,
                unit: "mm",
                format: pageFormat as any,
                compress: true
            });

            const [
                notoSansRegularBase64,
                notoSansBoldBase64,
                notoThaiRegularBase64,
                notoThaiBoldBase64,
                notoArabicRegularBase64,
                notoArabicBoldBase64,
                notoDevanagariRegularBase64,
                notoDevanagariBoldBase64
            ] = await Promise.all([
                loadFontAsBase64(NotoSansRegular),
                loadFontAsBase64(NotoSansBold),
                loadFontAsBase64(NotoSansThaiRegular),
                loadFontAsBase64(NotoSansThaiBold),
                loadFontAsBase64(NotoNaskhArabicRegular),
                loadFontAsBase64(NotoNaskhArabicBold),
                loadFontAsBase64(NotoSansDevanagariRegular),
                loadFontAsBase64(NotoSansDevanagariBold)
            ]);

            pdf.addFileToVFS("NotoSans-Regular.ttf", notoSansRegularBase64);
            pdf.addFont("NotoSans-Regular.ttf", "NotoSans", "normal");

            pdf.addFileToVFS("NotoSans-Bold.ttf", notoSansBoldBase64);
            pdf.addFont("NotoSans-Bold.ttf", "NotoSans", "bold");

            pdf.addFileToVFS("NotoSansThai-Regular.ttf", notoThaiRegularBase64);
            pdf.addFont("NotoSansThai-Regular.ttf", "NotoSansThai", "normal");

            pdf.addFileToVFS("NotoSansThai-Bold.ttf", notoThaiBoldBase64);
            pdf.addFont("NotoSansThai-Bold.ttf", "NotoSansThai", "bold");

            pdf.addFileToVFS("NotoNaskhArabic-Regular.ttf", notoArabicRegularBase64);
            pdf.addFont("NotoNaskhArabic-Regular.ttf", "NotoNaskhArabic", "normal");

            pdf.addFileToVFS("NotoNaskhArabic-Bold.ttf", notoArabicBoldBase64);
            pdf.addFont("NotoNaskhArabic-Bold.ttf", "NotoNaskhArabic", "bold");

            pdf.addFileToVFS("NotoSansDevanagari-Regular.ttf", notoDevanagariRegularBase64);
            pdf.addFont("NotoSansDevanagari-Regular.ttf", "NotoSansDevanagari", "normal");

            pdf.addFileToVFS("NotoSansDevanagari-Bold.ttf", notoDevanagariBoldBase64);
            pdf.addFont("NotoSansDevanagari-Bold.ttf", "NotoSansDevanagari", "bold");

            const pageSize = pdf.internal.pageSize;
            // const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
            const pageHeight = pageSize.height || pageSize.getHeight();
            const logoWidth = 30;
            const logoHeight = 15;
            let yOffset = 15;

            const loadImageFromURL = (url: string): Promise<string> => {
                return new Promise((resolve, reject) => {
                    const img = new Image();
                    img.crossOrigin = "Anonymous";
                    img.onload = () => {
                        const canvas = document.createElement("canvas");
                        canvas.width = img.width;
                        canvas.height = img.height;
                        const ctx = canvas.getContext("2d");
                        if (!ctx) return reject("Canvas error");
                        ctx.drawImage(img, 0, 0);
                        const dataURL = canvas.toDataURL("image/png");
                        resolve(dataURL);
                    };
                    img.onerror = (e) => reject("Image load error");
                    img.src = url;
                });
            };

            if (showCompanyLogo && companyLogo) {
                try {
                    if (isCompanyOwnLogo) {
                        const logoDataUrl = await loadImageFromURL(companyLogo);
                        pdf.addImage(logoDataUrl, 'PNG', 10, 5, logoWidth, logoHeight);
                    } else {
                        pdf.addImage(toAbsoluteUrl('/media/logos/LiveTecs_new-logo.png'), 'PNG', 10, 5, logoWidth, logoHeight);
                    }
                    yOffset = 5 + logoHeight + 5;
                } catch (error) {
                    console.warn("Logo Error", error);
                    pdf.addImage(toAbsoluteUrl('/media/logos/LiveTecs_new-logo.png'), 'PNG', 10, 5, logoWidth, logoHeight);
                    yOffset = 5 + logoHeight + 5;
                }
            }

            // pdf.setFontSize(14);
            // pdf.text(reportName, 10, yOffset);
            const reportNameText = String(reportName ?? "");
            pdf.setFont(getPDFFontForText(reportNameText),"bold");

            pdf.setFontSize(14);
            pdf.text(reportNameText, 10, yOffset);
            yOffset += 8;
            pdf.setFontSize(10);

            // if (selectedFilters && Object.keys(selectedFilters).length > 0) {
            //     pdf.text("Selected Filters:", 10, yOffset);
            //     yOffset += 6;
            //     pdf.setFontSize(9);
            //     Object.entries(selectedFilters).forEach(([key, value]) => {
            //         pdf.text(`${value}`, 10, yOffset);
            //         yOffset += 5;
            //     });
            //     yOffset += 4;
            // }
            if (selectedFilters && Object.keys(selectedFilters).length > 0) {
                pdf.setFont("NotoSans", "normal");
                pdf.setFontSize(10);
                pdf.text("Selected Filters:", 10, yOffset);
                yOffset += 6;
                pdf.setFontSize(9);
                Object.entries(selectedFilters).forEach(([key, value]) => {
                    const filterText = String(value ?? "");
                    pdf.setFont(getPDFFontForText(filterText), "normal");
                    pdf.text(filterText, 10, yOffset);
                    yOffset += 5;
                });
                yOffset += 4;
            }

            const alignmentByAccessor: Record<string, 'left' | 'right'> = {};
            const columnAlignments = config.columns?.map((col: any) => {
                const accessorKey = col.accessorKey?.toLowerCase() || '';
                const isNumeric = col.type?.toLowerCase() === "number" ||
                    col.type?.toLowerCase() === "decimal" ||
                    col.type?.toLowerCase() === "currencydecimal" ||
                    accessorKey.includes('basecurrency');
                // col.accessorKey === 'BaseCurrencyNetAmount';
                // return isNumeric ? 'right' : 'left';
                alignmentByAccessor[accessorKey] = isNumeric ? 'right' : 'left';
            }) || [];
            const columnAlignmentsByIndex = filteredHeaders.map((header) => {
                const headerText = header?.toLowerCase() || '';
                const matchedColumn = config.columns?.find(
                    (col: any) =>
                        col.header?.toLowerCase() === headerText ||
                        col.accessorKey?.toLowerCase() === headerText.replace(/\s+/g, '')
                );
                const accessorKey = matchedColumn?.accessorKey?.toLowerCase() || '';
                return alignmentByAccessor[accessorKey] || 'left';
            });
            const totalPagesExp = "{total_pages_count_string}";
            autoTable(pdf, {
                head: [filteredHeaders],
                body: filteredRows,
                foot: filteredFooterRows.every(row => row.every(cell => cell === ""))
                    ? undefined
                    : filteredFooterRows,
                startY: yOffset,
                margin: { left: 5, right: 5, top: 10, bottom: 10 },
                theme: 'grid',
                tableWidth: tableWidth ?? 'auto',
                // tableWidth: 'auto', 

                styles: {
                    fontSize: finalFontSize,
                    cellPadding: finalCellPadding,
                    overflow: "linebreak",
                    valign: "top",
                    lineColor: [200, 200, 200],
                    lineWidth: 0.1,
                    // font: "helvetica",
                    font: "NotoSans",
                    cellWidth: 'auto'
                },

                headStyles: {
                    fillColor: "black",
                    textColor: 255,
                    font: "NotoSans",
                    fontStyle: "bold",
                    fontSize: finalFontSize + 1,
                    halign: 'center',
                    valign: 'middle'
                },

                bodyStyles: {
                    textColor: [0, 0, 0],
                    font: "NotoSans",
                    fontStyle: "normal"
                },

                footStyles: {
                    fillColor: [240, 240, 240],
                    textColor: [0, 0, 0],
                    font: "NotoSans",
                    fontStyle: "bold",
                    fontSize: finalFontSize
                },

                showFoot: "lastPage",
                columnStyles: filteredHeaders.reduce((acc: any, header, index) => {
                    const headerText = header?.toLowerCase() || '';
                    const matchedColumn = config.columns?.find(
                        (col: any) =>
                            col.header?.toLowerCase() === headerText ||
                            col.accessorKey?.toLowerCase() === headerText.replace(/\s+/g, '')
                    );
                    const accessorKey = matchedColumn?.accessorKey?.toLowerCase() || '';
                    const isDescription = headerText.includes('description');

                    acc[index] = {
                        halign: alignmentByAccessor[accessorKey] || 'left',
                        cellWidth: isDescription ? 50 : 'auto',
                    };

                    return acc;
                }, {}),

                // columnStyles: filteredHeaders.reduce((acc: any, header, index) => {
                //     const isDescription = header && header.toLowerCase().includes('description');
                //     acc[index] = {
                //         halign: columnAlignments[index] || 'left',
                //         // Ensure wrapping is allowed per column
                //         cellWidth: isDescription ? 50 : 'auto'
                //     };
                //     return acc;
                // }, {}),

                // didParseCell: function (data: any) {
                //     if (data.section === 'foot') {
                //         const align = columnAlignmentsByIndex[data.column.index];
                //         if (align) data.cell.styles.halign = align;
                //     }
                // },
                
                didParseCell: function (data: any) {
                    const cellText = Array.isArray(data.cell.text)
                        ? data.cell.text.join(" ")
                        : String(data.cell.text ?? "");
                    const cleanText = cellText.replace(
                        /\u200F|\u200E|\u202A|\u202B|\u202C|\u202D|\u202E/g,
                        ""
                    );
                    const fontName = getPDFFontForText(cleanText);
                    data.cell.styles.font = fontName;
                    if (data.section === "head" || data.section === "foot") {
                        data.cell.styles.fontStyle = "bold";
                    } else {
                        data.cell.styles.fontStyle = "normal";
                    }
                    if (data.section === "foot") {
                        const align = columnAlignmentsByIndex[data.column.index];
                        if (align) {
                            data.cell.styles.halign = align;
                        }
                    }
                },

                // didDrawPage: function (data: any) {
                //     pdf.setFontSize(8);
                //     pdf.setTextColor(100);
                //     pdf.text(
                //         `Page ${data.pageNumber} of ${totalPagesExp}`,
                //         data.settings.margin.left,
                //         pageHeight - 5
                //     );
                // },

                didDrawPage: function (data: any) {
                        pdf.setFont("NotoSans", "normal");
                        pdf.setFontSize(8);
                        pdf.setTextColor(100);

                        pdf.text(
                            `Page ${data.pageNumber} of ${totalPagesExp}`,
                            data.settings.margin.left,
                            pageHeight - 5
                        );
                    },
                });

            document.body.removeChild(tempContainer);
            if (typeof pdf.putTotalPages === 'function') {
                pdf.putTotalPages(totalPagesExp);
            }
            table.setPagination(originalPagination);
            pdf.save("LiveReport.pdf");

        } catch (error) {
            console.log(error);
        } finally {
            setExportLoading(prev => ({ ...prev, pdf: false }));
        }
    };

    const handleExportXLS = async () => {
        setExportLoading(prev => ({ ...prev, xls: true }));
        try {
            const originalPagination = {
                pageIndex: table.getState().pagination.pageIndex,
                pageSize: table.getState().pagination.pageSize,
            };
            const totalRows = table.getPrePaginationRowModel().rows.length;
            table.setPagination({
                pageIndex: 0,
                pageSize: totalRows,
            });
            await new Promise(resolve => setTimeout(resolve, 500));
            const tableElement = document.querySelector('.MuiTable-root');
            if (!tableElement) return;
 
            const wb = XLSX.utils.book_new();
            const wsData: any[][] = [];
 
            wsData.push([reportName]);
 
            if (selectedFilters && Object.keys(selectedFilters).length > 0) {
                Object.entries(selectedFilters).forEach(([key, value]) => {
                    wsData.push([value]);
                });
            }
 
            wsData.push([]);
 
            const headerRows = Array.from(tableElement.querySelectorAll('thead tr'));
            const headers = headerRows[headerRows.length - 1]
                ? Array.from(headerRows[headerRows.length - 1].querySelectorAll('th')).map(th => {
                    let text = th.textContent?.trim() || '';
                    return text.endsWith('0') ? text.slice(0, -1) : text;
                })
                : [];
 
            const rows = Array.from(tableElement.querySelectorAll('tbody tr')).map(tr =>
                Array.from(tr.querySelectorAll('td')).map(td => {
                    const text = td.textContent?.trim() || '';
                    return text;
                })
            );
 
            const footerRows = Array.from(tableElement.querySelectorAll('tfoot tr')).map(tr =>
                Array.from(tr.querySelectorAll('td')).map(td => td.textContent?.trim() || '')
            );
 
            let expandColumnIndex = -1;
            if (consolidated) {
                expandColumnIndex = headers.findIndex(header => header === '' || header.toLowerCase().includes('expand'));
            }
 
            const emptyColumns = headers.map((header, index) =>
                (!header && rows.every(row => !row[index]) && footerRows.every(row => !row[index])) ||
                (consolidated && index === expandColumnIndex)
            );
 
            const filteredHeaders = headers.filter((_, index) => !emptyColumns[index]);
            let filteredRows = rows.map(row => row.filter((_, index) => !emptyColumns[index]));
            filteredRows = filteredRows.filter(row => row.some(cell => cell && cell.trim() !== ''));
            const filteredFooterRows = footerRows.map(row => row.filter((_, index) => !emptyColumns[index]));
 
            wsData.push(filteredHeaders);
            filteredRows.forEach(row => wsData.push(row));
            filteredFooterRows.forEach(row => wsData.push(row));
 
            const ws = XLSX.utils.aoa_to_sheet(wsData);
 
            ws['!cols'] = filteredHeaders.map(header => {
                if (header.toLowerCase().includes('description')) return { wch: 40 };
                if (header.toLowerCase().includes('name')) return { wch: 25 };
                return { wch: 20 };
            });
 
            if (ws['!ref']) {
                const range = XLSX.utils.decode_range(ws['!ref']);
                const mrtRows = table.getRowModel().rows;
 
                const columnAlignments = filteredHeaders.map((header, colIndex) => {
                    const columnDef = finalColumns.find(col =>
                        col.header === header || col.accessorKey === header || col.id === header
                    );
 
                    if (columnDef) {
                        const type = columnDef.type?.toLowerCase();
                        const accessor = columnDef.accessorKey?.toLowerCase();
                        if (
                            type === 'currencydecimal' ||
                            type === 'decimal' ||
                            type === 'number' ||
                            (accessor && [
                                'basecurrencynetamount',
                                'basecurrencyemployeecost',
                                'basecurrencyprofit',
                                'basecurrencyamount'
                            ].includes(accessor))
                        ) {
                            return 'right';
                        }
                    }
 
                    return 'left';
                });
 
                filteredRows.forEach((rowData, index) => {
                    const mrtRow = mrtRows[index];
                    const excelRowIndex = index + wsData.length - filteredRows.length - filteredFooterRows.length;
 
                    for (let c = 0; c < rowData.length; c++) {
                        const cellAddress = XLSX.utils.encode_cell({ r: excelRowIndex, c });
                        if (!ws[cellAddress]) ws[cellAddress] = { v: rowData[c] };
                        const hasComma = rowData[c] && rowData[c].includes(',');
                        const valueWithoutComma = hasComma ? rowData[c].replace(',', '.') : rowData[c];
                        const isNumeric = columnAlignments[c] === 'right' && !isNaN(parseFloat(valueWithoutComma)); 
                        if (isNumeric && hasComma) {
                            ws[cellAddress].v = rowData[c];
                            ws[cellAddress].t = 's'
                        } else if (isNumeric) {
                            ws[cellAddress].v = parseFloat(rowData[c]);
                            ws[cellAddress].t = 'n';
                            ws[cellAddress].z = '0.00';
                        } 
                        const isGroupedRow = mrtRow?.getIsGrouped();
                        const isConsolidated = consolidated || isGroupedRow;
 
                        ws[cellAddress].s = {
                            fill: isConsolidated ? { fgColor: { rgb: "F5F5F5" } } : undefined,
                            font: isConsolidated ? { bold: true } : undefined,
                            alignment: {
                                horizontal: columnAlignments[c],
                                vertical: "center",
                                wrapText: true
                            },
                            border: {
                                top: { style: 'thin', color: { rgb: "A6A6A6" } },
                                left: { style: 'thin', color: { rgb: "A6A6A6" } },
                                bottom: { style: 'thin', color: { rgb: "A6A6A6" } },
                                right: { style: 'thin', color: { rgb: "A6A6A6" } }
                            }
                        };
                    }
                });
 
                for (let C = range.s.c; C <= range.e.c; ++C) {
                    const headerCell = XLSX.utils.encode_cell({
                        r: wsData.length - filteredRows.length - filteredFooterRows.length - 1,
                        c: C
                    });
 
                    if (!ws[headerCell]) continue;
 
                    ws[headerCell].s = {
                        fill: { fgColor: { rgb: "000000" } },
                        font: { color: { rgb: "FFFFFF" }, bold: true },
                        alignment: { horizontal: "center", vertical: "center", wrapText: true },
                        border: {
                            top: { style: 'thin', color: { rgb: "A6A6A6" } },
                            left: { style: 'thin', color: { rgb: "A6A6A6" } },
                            bottom: { style: 'thin', color: { rgb: "A6A6A6" } },
                            right: { style: 'thin', color: { rgb: "A6A6A6" } }
                        }
                    };
                }
 
                for (let i = 0; i < filteredFooterRows.length; i++) {
                    const footerRow = filteredFooterRows[i];
                    const isEmptyFooterRow = footerRow.every(cell => !cell || cell.trim() === '');
                    if (isEmptyFooterRow) continue;
 
                    for (let C = range.s.c; C <= range.e.c; ++C) {
                        const footerCell = XLSX.utils.encode_cell({
                            r: wsData.length - filteredFooterRows.length + i,
                            c: C
                        });
 
                        if (!ws[footerCell]) continue;
   const hasComma = filteredFooterRows[i][C] && filteredFooterRows[i][C].includes(',');
                        const valueWithoutComma = hasComma ? filteredFooterRows[i][C].replace(',', '.') : filteredFooterRows[i][C];
                        const isNumeric = columnAlignments[C] === 'right' && !isNaN(parseFloat(valueWithoutComma));
 
                        if (isNumeric && hasComma) {
                            ws[footerCell].v = filteredFooterRows[i][C];
                            ws[footerCell].t = 's'
                        } else if (columnAlignments[C] === 'right' && !isNaN(parseFloat(footerRow[C]))) {
                            ws[footerCell].v = parseFloat(footerRow[C]);
                            ws[footerCell].t = 'n';
                            ws[footerCell].z = '0.00';
                        } 
                        ws[footerCell].s = {
                            // fill: { fgColor: { rgb: "F5F5F5" } },
                            // font: { bold: true },
                            fill: { fgColor: { rgb: "000000" } },
                            font: { color: { rgb: "FFFFFF" }, bold: true },
                            alignment: {
                                horizontal: columnAlignments[C],
                                vertical: "center",
                                wrapText: true
                            },
                            border: {
                                top: { style: 'thin', color: { rgb: "A6A6A6" } },
                                left: { style: 'thin', color: { rgb: "A6A6A6" } },
                                bottom: { style: 'thin', color: { rgb: "A6A6A6" } },
                                right: { style: 'thin', color: { rgb: "A6A6A6" } }
                            }
                        };
                    }
                }
            }
 
            XLSX.utils.book_append_sheet(wb, ws, "Report");
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            const fileName = `LiveReport_${timestamp}.xlsx`;
            // XLSX.writeFile(wb, fileName);
            const wbout = XLSX.write(wb, {
                bookType: "xlsx",
                type: "array",
                compression: true
            });
            const blob = new Blob([wbout], { type: "application/octet-stream" });
            saveAs(blob, fileName);
            table.setPagination(originalPagination);
        } catch (error) {
            console.log(error);
        } finally {
            setExportLoading(prev => ({ ...prev, xls: false }));
        }
    };
   
    let tableDetail;
    if (consolidated) {
        tableDetail = "group"
    } else {
        tableDetail = "details"
    }

    useEffect(() => {
        if (consolidated) {
            table.setGrouping(groupKeys);
            table.setExpanded(true);
        } else {
            table.setGrouping(groupKeys);
            table.setExpanded(true);
        }
    }, [tableDetail]);

    const handlePDFSubmit = (settings: any) => {
        handleExportPDF(settings);
        setIsOpen(false);
    };

    const onClose = () => {
        setIsOpen(false);
    }

    return (
        <>
            <MaterialReactTable table={table} />
            {isOpen && <ExportToPDFModal isOpen={isOpen} onClose={onClose} onSubmit={handlePDFSubmit} />}
        </>
    )
}

export default MUITableView;
